import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { ANIMATION_DURATION, ANIMATION_GAP } from './animation-schedule-constants';
import { AnimationScheduleContext, useAnimationSchedule } from './animation-schedule-context';
import { BaseScheduledNode } from './base-scheduled-node';
import { MarkdownScheduledNode } from './markdown-scheduled-node';
import { RevealBoundary } from './reveal-boundary';
import { TextChunkAnimation } from './text-chunk-animation';

export { ANIMATION_DURATION, ANIMATION_GAP } from './animation-schedule-constants';

export function AnimationSchedule({ children = null, isReadyToComplete = false, onAnimationComplete = () => {} }) {
  const animationUnitsRef = useRef(new Map());
  const nextStartAtRef = useRef(0);
  const latestCompletionAtRef = useRef(0);

  const requestAnimation = useCallback((unitId, options = {}) => {
    const { duration = ANIMATION_DURATION, blocksFollowing = false } = options;
    const existingUnit = animationUnitsRef.current.get(unitId);

    if (existingUnit) {
      return {
        ...existingUnit,
        delay: Math.max(0, existingUnit.startAt - Date.now()),
      };
    }

    const now = Date.now();
    const startAt = Math.max(now, nextStartAtRef.current, blocksFollowing ? latestCompletionAtRef.current : now);
    const endAt = startAt + duration;
    const unit = { delay: Math.max(0, startAt - now), startAt, endAt };

    animationUnitsRef.current.set(unitId, unit);
    nextStartAtRef.current = startAt + ANIMATION_GAP;
    latestCompletionAtRef.current = Math.max(latestCompletionAtRef.current, endAt);

    return unit;
  }, []);

  useEffect(() => {
    if (!isReadyToComplete) {
      return undefined;
    }

    const delay = Math.max(0, latestCompletionAtRef.current - Date.now());
    const completionTimer = setTimeout(onAnimationComplete, delay);

    return () => clearTimeout(completionTimer);
  }, [isReadyToComplete, onAnimationComplete]);

  const contextValue = useMemo(() => ({ requestAnimation }), [requestAnimation]);

  return <AnimationScheduleContext.Provider value={contextValue}>{children}</AnimationScheduleContext.Provider>;
}

AnimationSchedule.BaseScheduledNode = BaseScheduledNode;
AnimationSchedule.MarkdownScheduledNode = MarkdownScheduledNode;
AnimationSchedule.RevealBoundary = RevealBoundary;
AnimationSchedule.TextChunkAnimation = TextChunkAnimation;

AnimationSchedule.propTypes = {
  children: PropTypes.node,
  isReadyToComplete: PropTypes.bool,
  onAnimationComplete: PropTypes.func,
};

export { useAnimationSchedule };
